'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Think Dirty vs Yuka: How the Two Scanners Differ — and the Question Neither One Asks"
      author="MicroPlastics Research Desk"
      date="August 14, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/general.webp"
      heroImageAlt="Think Dirty vs Yuka compared in 2026: scoring methods, database size, and the synthetic polymer question neither app rates"
    >
      <BlogLastUpdated iso="2026-08-14" formatted="August 14, 2026" />

      <BlogTLDR>
        <strong>They are both hazard scanners, and they disagree because they
        weight hazard differently — not because one of them is broken.</strong>{' '}
        Yuka scores <strong>0&ndash;100</strong> and is driven by the single
        worst ingredient in the formula: one red-flagged ingredient caps the
        product below 25/100 regardless of what else is in it. Think Dirty
        scores <strong>0&ndash;10</strong> on its Dirty Meter and leans
        noticeably harder on <em>natural is safer</em>, which means it will
        penalise well-formulated synthetic products that Yuka lets through.
        Yuka has the far bigger catalogue — roughly{' '}
        <strong>1.5 million food and 500,000 cosmetic products</strong> against
        Think Dirty&rsquo;s ~1,200 brands. Both share the same real limitation:{' '}
        <strong>they rate an ingredient&rsquo;s presence, not its
        concentration.</strong> And there is one question neither is built to
        answer. Yuka&rsquo;s own published criteria are endocrine disruption,
        allergenic, irritant, carcinogenic and pollutant.{' '}
        <strong>Synthetic polymers are on none of those lists</strong>, because
        they are not acutely toxic — so a product can score beautifully on both
        apps and still be built almost entirely out of plastic.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Yuka: 0&ndash;100, worst-ingredient-wins.</strong> A single high-risk ingredient forces the score below 25/100; a moderate-risk one caps it below 50. Penalties run &minus;10 for carcinogens and endocrine disruptors, &minus;7 for allergens and irritants, &minus;2 for a single risk.</>,
          <><strong>Think Dirty: 0&ndash;10 Dirty Meter</strong>, judged on chemical content alone — explicitly not brand reputation or marketing claims.</>,
          <><strong>Database size is the biggest practical difference.</strong> Yuka covers roughly 1.5M food and 500k cosmetic products; Think Dirty lists around 1,200 brands, so it returns &ldquo;not found&rdquo; far more often.</>,
          <><strong>Think Dirty is the more cautious of the two</strong> and over-flags competent synthetic formulations. Yuka is broader and more evidence-leaning, but still watchlist-driven.</>,
          <><strong>Neither accounts for concentration.</strong> An ingredient at 0.01% and at 20% are scored the same way. This is the criticism cosmetic chemists make of the whole category, and it is a fair one.</>,
          <><strong>Neither rates synthetic polymers.</strong> Yuka&rsquo;s stated risk categories are endocrine disruption, allergenic, irritant, carcinogenic and pollutant. Acrylates copolymer, nylon-12 and PET are toxicologically inert, so they trigger no penalty.</>,
          <><strong>That is a scope difference, not an error.</strong> Microplastics are a persistence and environmental problem rather than an acute-toxicity one, and a hazard scanner is the wrong instrument for finding them.</>,
        ]}
      />

      <KeyStats
        heading="The two apps, by the numbers"
        stats={[
          { value: '0–100', label: 'Yuka’s scale', context: 'set by the highest-risk ingredient present, not by an average across the formula', source: 'Yuka Help — How are cosmetic products evaluated?', sourceUrl: 'https://help.yuka.io/l/en/article/2t20ixn5y5-evualuation-cosmetic-products' },
          { value: '0–10', label: 'Think Dirty’s Dirty Meter', context: 'lower is cleaner; judged on chemical content alone, not brand or marketing', source: 'Think Dirty', sourceUrl: 'https://apps.apple.com/app/id687176839' },
          { value: '~2M', label: 'products in Yuka’s catalogue', context: 'roughly 1.5M food and 500k cosmetics — the practical reason it returns fewer blanks', source: 'Suvarna, Think Dirty vs INCI Beauty, Yuka and more', sourceUrl: 'https://suvarna.co.uk/blogs/news/the-think-dirty-app-vs-inci-beauty-yuka-and-more-your-insider-guide-to-clean-beauty-tech' },
          { value: '5', label: 'risk categories Yuka rates', context: 'endocrine disruption, allergenic, irritant, carcinogenic, pollutant — synthetic polymers are not among them', source: 'Yuka Help — How are cosmetic products evaluated?', sourceUrl: 'https://help.yuka.io/l/en/article/2t20ixn5y5-evualuation-cosmetic-products' },
          { value: '0%', label: 'weight either app gives to concentration', context: 'presence on a watchlist drives the score; the percentage in the formula does not', source: 'Cosmetic chemists on ingredient apps, NewBeauty', sourceUrl: 'https://www.newbeauty.com/view/beauty-ingredient-apps' },
        ]}
      />

      <h2>How each one actually decides</h2>
      <p>
        The reason two apps hand you different verdicts on the same bottle is
        not that one has better data. It is that they are running different
        rules, and both rules are defensible.
      </p>
      <p>
        <strong>Yuka is a worst-ingredient system.</strong> It reads the full
        INCI list, assigns each ingredient to a risk band, and then lets the
        single worst one set the ceiling. One red-flagged ingredient and the
        product cannot score above 25 out of 100, no matter how unobjectionable
        the other thirty ingredients are. That is deliberately unforgiving, and
        it is why a product can drop from 90 to 20 on the strength of one
        preservative.
      </p>
      <p>
        <strong>Think Dirty is a chemical-content system with a philosophical
        lean.</strong> It rates on what is in the formula rather than on brand
        reputation or marketing claims, which is genuinely to its credit — it
        will not be talked round by the word &ldquo;clean&rdquo; on the front of
        the box. But its underlying view tends toward <em>natural is
        safer</em>, so competent synthetic formulations get marked down in ways
        a cosmetic chemist would often dispute.
      </p>

      <BlogComparisonTable
        caption="Think Dirty and Yuka compared on the things that actually change your answer (checked August 2026)"
        columns={[
          { key: 'f', label: 'Feature' },
          { key: 'td', label: 'Think Dirty' },
          { key: 'y', label: 'Yuka' },
        ]}
        rows={[
          { f: 'Scale', td: '0–10 Dirty Meter (lower is cleaner)', y: '0–100 (higher is better)' },
          { f: 'How the score is set', td: 'Chemical content of the formula', y: 'The single highest-risk ingredient present' },
          { f: 'Scope', td: 'Personal care, household, baby, pet', y: 'Food and cosmetics' },
          { f: 'Catalogue', td: '~1,200 brands — more blanks', y: '~1.5M food + ~500k cosmetics' },
          { f: 'Philosophical lean', td: 'Natural-leaning; over-flags synthetics', y: 'Watchlist-driven; broader, more evidence-leaning' },
          { f: 'Accounts for concentration?', td: 'No', y: 'No' },
          { f: 'Rates synthetic polymers?', td: 'No', y: 'No — not among its five risk categories' },
          { f: 'Notable alignment', td: 'Partners with Breast Cancer Prevention Partners', y: 'Independent; funded by premium subscriptions' },
        ]}
      />

      <InlineGuessReveal
        source="think_dirty_vs_yuka_polymers"
        question="A face cream is 30% acrylates copolymer — a synthetic polymer. What do Yuka and Think Dirty say?"
        options={[
          { label: 'Both flag it as a microplastic' },
          { label: 'Yuka flags it, Think Dirty does not' },
          { label: 'Neither flags it — it can still score well', correct: true },
          { label: 'Both refuse to score the product' },
        ]}
        answer="Neither flags it"
        answerNote={
          <>
            Yuka&rsquo;s published risk categories are endocrine disruption,
            allergenic, irritant, carcinogenic and pollutant. Acrylates
            copolymer is none of those things — it is biologically inert, which
            is precisely why formulators like it and precisely why it persists
            in the environment. The property that makes it a microplastics
            problem is the same property that makes it look harmless to a
            toxicological hazard scanner.
          </>
        }
        ctaText="Inert on a hazard list is not the same as absent from the formula."
        ctaLabel="Scan for polymers"
      />

      <RiskQuizInlineNudge source="blog_inline_think_dirty_vs_yuka" />

      <h2>The criticism they share, stated fairly</h2>
      <p>
        Both apps attract the same objection from cosmetic chemists, and it is
        worth taking seriously rather than waving away.{' '}
        <strong>Hazard is not the same as risk.</strong> Hazard is what a
        substance could do in principle; risk is what it will do at the dose you
        actually encounter. Neither app models dose.
      </p>
      <p>
        So an ingredient present at a fraction of a percent, within a legally
        permitted limit, is scored as though it were the dominant component. A
        formula can be penalised for a preservative that exists specifically to
        stop the product growing bacteria — a real, immediate hazard that
        neither app scores at all, because microbial contamination does not
        appear on an ingredient list.
      </p>
      <p>
        This cuts both ways and we are not going to pretend otherwise. It is a
        genuine methodological weakness, and it applies to{' '}
        <em>any</em> tool that reads a label rather than a lab result — which
        includes ingredient-level microplastics screening. What a label-reading
        tool can tell you reliably is <strong>what is in the product</strong>.
        What it cannot tell you is <strong>how much of it will reach
        you</strong>. Every honest scanner is on the same side of that line.
      </p>

      <h2>The question neither app is built to ask</h2>
      <p>
        Here is the gap, and it is a gap of scope rather than of quality.
      </p>
      <p>
        Yuka rates five things: endocrine disruption, allergenic potential,
        irritancy, carcinogenicity and pollution. That is a list of{' '}
        <strong>toxicological hazards</strong> — ways a molecule can interfere
        with a body. It is a sensible list, and for its purpose it works.
      </p>
      <p>
        Now consider what a cosmetic microplastic actually is. Acrylates
        copolymer, nylon-12, polyethylene, PET glitter, polyurethane-11.{' '}
        <strong>These are not endocrine disruptors. They are not carcinogens or
        irritants. They are biologically inert — that is the entire point of
        them.</strong> A polymer is chosen precisely because it does not react
        with skin, does not degrade, and holds a film together indefinitely.
      </p>
      <p>
        Which means the property that makes a synthetic polymer a{' '}
        <em>microplastics</em> problem — that it persists, does not break down,
        and accumulates — is invisible to a scanner looking for toxicity. It
        will pass every check, because the checks are looking for something
        else. A rinse-off exfoliant built on polyethylene beads can be, in
        strict toxicological terms, entirely unremarkable.
      </p>
      <p>
        That is not Yuka being wrong or Think Dirty being careless. It is the
        wrong instrument for the measurement, in the same way a thermometer is
        not a poor scale. See{' '}
        <Link href="/blog/clean-beauty-microplastics-loophole" className="text-forest underline">the clean beauty loophole</Link>{' '}
        for how the same blind spot works in marketing claims, and{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-forest underline">the worst microplastic ingredients</Link>{' '}
        for the INCI names that carry it.
      </p>

      <BlogAppCTA
        headline="The polymer question, answered"
        sub="MicroPlastics reads the same ingredient list and asks the one thing a hazard scanner does not: which of these are synthetic polymers. Scan a product for a 0–100 score on plastic content specifically."
      />

      <h2>Which one should you actually use</h2>
      <ul>
        <li>
          <strong>For food, Yuka, without much argument.</strong> Think Dirty
          does not cover food, and Yuka&rsquo;s catalogue depth means it will
          usually return an answer rather than a blank.
        </li>
        <li>
          <strong>For cosmetics, Yuka finds more products; Think Dirty is
          stricter.</strong> If you want the cautious read and do not mind
          gaps, Think Dirty. If you want coverage and a slightly more
          evidence-weighted verdict, Yuka.
        </li>
        <li>
          <strong>Treat a bad score as a prompt, not a verdict.</strong> Look
          at <em>which</em> ingredient caused it. A product capped at 20/100 by
          one preservative present at 0.1% is a different proposition from one
          capped by three separate flagged ingredients.
        </li>
        <li>
          <strong>Do not read a good score as &ldquo;no plastic&rdquo;.</strong>{' '}
          This is the practical takeaway. A high Yuka score and a low Dirty
          Meter rating both mean &ldquo;nothing on our hazard list&rdquo;. They
          do not mean the formula is free of synthetic polymers, and they are
          not claiming to.
        </li>
        <li>
          <strong>Use two tools if the polymer question matters to you.</strong>{' '}
          Hazard and plastic content are genuinely separate questions, and no
          single score collapses them honestly. We would rather say that than
          pretend one number covers everything.
        </li>
      </ul>

      <p>
        For how we compare directly against Yuka on the microplastics question,
        see{' '}
        <Link href="/vs/yuka" className="text-forest underline">MicroPlastics vs Yuka</Link>.
        For the ingredient names to recognise on a label, see{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-forest underline">microplastics in cosmetics</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Is Think Dirty or Yuka more accurate?',
            answer: 'Neither is straightforwardly more accurate, because they optimise for different things. Yuka has a far larger catalogue — around 1.5 million food and 500,000 cosmetic products — and sets a product’s score by its single highest-risk ingredient. Think Dirty rates on chemical content alone and leans toward treating natural ingredients as safer, which means it flags competent synthetic formulations more often. Yuka is generally the more evidence-leaning of the two; Think Dirty is the more cautious. Both rate hazard rather than exposure, so neither accounts for how much of an ingredient is actually present.',
          },
          {
            question: 'Why do Think Dirty and Yuka give different scores for the same product?',
            answer: 'Because they use different rules rather than different facts. Yuka is a worst-ingredient system: one high-risk ingredient caps the score below 25 out of 100 regardless of the rest of the formula. Think Dirty scores the chemical content overall and applies a natural-leaning philosophy, so it penalises synthetic ingredients that Yuka may treat as low risk. The same INCI list can therefore produce a good Yuka score and a poor Dirty Meter rating, or the reverse.',
          },
          {
            question: 'Do Yuka or Think Dirty detect microplastics?',
            answer: 'No. Yuka’s published risk categories are endocrine disruption, allergenic, irritant, carcinogenic and pollutant, and synthetic polymers are not among them. Ingredients such as acrylates copolymer, nylon-12, polyethylene and PET are biologically inert, so they do not trigger a toxicological penalty in either app. A product can score highly on both while being largely built from synthetic polymers. This is a difference in scope rather than a flaw: microplastics are a persistence and environmental issue, not an acute-toxicity one.',
          },
          {
            question: 'Is there a better app than Yuka?',
            answer: 'It depends entirely on the question you are asking. For general food and cosmetic hazard screening, Yuka’s catalogue depth is hard to beat and there is no strong reason to switch. For the specific question of which ingredients are synthetic polymers — the microplastics question — a hazard scanner is the wrong tool regardless of how good it is, because inert polymers are invisible to hazard criteria by design. Those are complementary tools rather than competing ones.',
          },
          {
            question: 'Why do cosmetic chemists criticise ingredient scanning apps?',
            answer: 'The central objection is that hazard is not risk. These apps score an ingredient by its presence on a watchlist rather than by the concentration in the formula, so a substance at 0.01% is treated like one at 20%. They also cannot account for formulation context — a preservative may be flagged despite existing to prevent microbial growth, which is a real hazard the apps do not score at all because it does not appear on an ingredient list. The criticism is legitimate and applies to any tool reading a label rather than a laboratory result.',
          },
          {
            question: 'Is Think Dirty free?',
            answer: 'Think Dirty is free to download and scan, as is Yuka, with both offering paid tiers for additional features. The more consequential difference for most people is not price but coverage: Think Dirty lists roughly 1,200 brands and returns "not found" considerably more often than Yuka, whose catalogue runs to around two million products across food and cosmetics.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Yuka', year: 2026, title: 'How are cosmetic products evaluated? — risk bands (high, moderate, low, risk-free) and the five assessed effects: endocrine disruption, allergenic, irritant, carcinogenic, pollutant; score set by the highest-risk ingredient present', publisher: 'Yuka Help Centre', url: 'https://help.yuka.io/l/en/article/2t20ixn5y5-evualuation-cosmetic-products' },
          { authors: 'Yuka', year: 2026, title: 'How are penalties calculated in the cosmetic product scores? — −10 for potential carcinogens and endocrine disruptors, −7 for allergen and irritant risks, −2 for a single risk', publisher: 'Yuka Help Centre', url: 'https://help.yuka.io/l/en/article/ih5pet4ffc-how-are-penalties-calculated-in-the-cosmetic-product-scores' },
          { authors: 'Suvarna', year: 2026, title: 'The Think Dirty app vs INCI Beauty, Yuka and more — catalogue sizes and scoring philosophy compared', publisher: 'Suvarna', url: 'https://suvarna.co.uk/blogs/news/the-think-dirty-app-vs-inci-beauty-yuka-and-more-your-insider-guide-to-clean-beauty-tech' },
          { authors: 'NewBeauty', year: 2026, title: 'What cosmetic chemists have to say about Yuka and other ingredient apps — hazard versus dose, and the concentration objection', publisher: 'NewBeauty', url: 'https://www.newbeauty.com/view/beauty-ingredient-apps' },
          { authors: 'Glossy', year: 2026, title: 'The rise of the Gen-Z-loved Yuka app — and its growing cohort of critics', publisher: 'Glossy', url: 'https://www.glossy.co/beauty/yuka-beauty-wellness-product-scanning-app/' },
        ]}
      />
    </BlogPostLayout>
  )
}
